import { useEffect, useEffectEvent, useRef } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';
import { useGenUiConfig } from '../../../../shared/ui/config.js';

/** O botão vira a bolinha (#5279, artboard «Animação do carrinho»): 200 ms,
 *  do retângulo ao ponto de 12 px no MESMO centro, já na cor da marca (o
 *  «Solicitando» travado é cinza), de onde o host a leva até a viagem. */
const DOT_PX = 12;
const SHRINK_MS = 200;

function shrinkFrames(button: HTMLButtonElement): Keyframe[] {
  const { width, height } = button.getBoundingClientRect();
  const to = `scale(${String(DOT_PX / width)}, ${String(DOT_PX / height)})`;
  return [
    { transform: 'scale(1)', color: 'inherit' },
    {
      transform: to,
      borderRadius: '999px',
      color: 'transparent',
      backgroundColor: 'var(--brand)',
    },
  ];
}

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Encolhe o botão (sem movimento reduzido e com WAAPI). `null` ⇒ sem
 *  encolher: a chegada parte já. */
function shrink(button: HTMLButtonElement): Animation | null {
  // Sem WAAPI (jsdom) antes do `matchMedia`, que o jsdom também não tem.
  if (typeof button.animate !== 'function' || prefersReducedMotion()) return null;
  return button.animate(shrinkFrames(button), {
    duration: SHRINK_MS,
    easing: 'cubic-bezier(.32,0,.67,0)',
    fill: 'forwards',
  });
}

/** «Solicitando» no momento em que o pedido entrou: o botão encolhe até a
 *  bolinha e entrega o ponto de partida ao host (`onArrive`), que a leva até a
 *  viagem. Quando ela chega (ou sem host que a leve), `onArrived` vira o card no
 *  registro. Movimento reduzido, ou a viagem já à vista (`arrivesInline`): sem
 *  encolher; o host decide a própria chegada. */
export function OneClickArrivingButton({
  label,
  className,
  onArrive,
  onArrived,
}: {
  readonly label: string;
  readonly className: string;
  readonly onArrive: ((from: DOMRect) => Promise<void>) | undefined;
  readonly onArrived: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const arrivesInline = useGenUiConfig().oneClick?.arrivesInline;
  // A chegada é um gesto só, montado uma vez: um re-render do card no meio dela não
  // pode reiniciá-la. Os callbacks são lidos na hora em que cada passo acontece.
  const isInline = useEffectEvent(() => arrivesInline?.() ?? false);
  const arrive = useEffectEvent((from: DOMRect) => onArrive?.(from));
  const arrived = useEffectEvent(() => {
    onArrived();
  });
  useEffect(() => {
    const button = ref.current;
    if (!button) return undefined;
    let live = true;
    // Com a viagem à vista nada sai do card (#6329): sem bolinha, o registro
    // entra no lugar do «Solicitando».
    const animation = isInline() ? null : shrink(button);
    (animation ? animation.finished : Promise.resolve())
      .then(() => {
        // Desmontado no meio (o StrictMode monta duas vezes): a chegada é do
        // efeito que ficou, nunca das duas.
        if (!live) return undefined;
        // A bolinha que segue é a do host: a deste botão some, ou seriam duas.
        if (animation) button.style.visibility = 'hidden';
        return arrive(button.getBoundingClientRect());
      })
      // Uma chegada que falhou ainda é um pedido feito: o registro aparece.
      .catch(() => undefined)
      .finally(() => {
        if (live) arrived();
      });
    return () => {
      live = false;
      animation?.cancel();
    };
  }, []);
  return (
    <Button ref={ref} size="xl" aria-busy="true" aria-disabled="true" className={className}>
      {label}
      <Spinner aria-hidden />
    </Button>
  );
}
